import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { UserContext } from '../context/UserContext';
import { AuthService } from '../services/authService';
import { ROUTES } from './constants';
import Loading from '../components/common/Loading';
import ServerUnreachable from '../components/common/ServerUnreachable';

function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}

const ProtectedRoutes = () => {
  const { user, loading, loadError, fetchUser } = useUserContext();
  const location = useLocation();
  const isAuthenticated = AuthService.isAuthenticated();

  useEffect(() => {
    // loadError gates the refetch: without it, a transient failure would
    // re-trigger this effect forever (fetch → fail → !user && !loading → fetch).
    if (isAuthenticated && !user && !loading && !loadError) {
      fetchUser();
    }
  }, [isAuthenticated, user, loading, loadError, fetchUser]);

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.SIGNIN} state={{ from: location }} replace />;
  }

  if (!user && loadError) {
    // Transient failure with tokens kept (see UserContext loadError) —
    // recovery is a user-initiated retry, never an automatic loop.
    return <ServerUnreachable onRetry={fetchUser} />;
  }

  if (!user) {
    return <Loading />;
  }

  return <Outlet />;
};

export default ProtectedRoutes;
